import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, TrendingUp, TrendingDown } from "lucide-react";
import { Modal, Field } from "@/pages/Agenda";
import { brl, fmtDateBR, todayISO } from "@/lib/format";
import { useSearchParams } from "react-router-dom";
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip } from "recharts";

export default function Financeiro() {
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [preset, setPreset] = useState("receita");
  const [params] = useSearchParams();

  const load = async () => setItems((await api.get("/transactions")).data);
  useEffect(() => {
    load();
    const newP = params.get("new");
    if (newP === "receita" || newP === "despesa") { setPreset(newP); setShowModal(true); }
  // eslint-disable-next-line
  }, []);

  const receitas = items.filter(i => i.type === "receita").reduce((a, b) => a + b.amount, 0);
  const despesas = items.filter(i => i.type === "despesa").reduce((a, b) => a + b.amount, 0);

  const chartData = useMemo(() => {
    const now = new Date();
    const months = [];

    for (let i = 5; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
        label: d.toLocaleDateString("pt-BR", { month: "short" }).replace(".", ""),
        receitas: 0,
        despesas: 0,
      });
    }

    const byKey = Object.fromEntries(months.map(m => [m.key, m]));
    items.forEach(item => {
      const key = String(item.date || "").slice(0, 7);
      if (!byKey[key]) return;
      if (item.type === "receita") byKey[key].receitas += Number(item.amount || 0);
      if (item.type === "despesa") byKey[key].despesas += Number(item.amount || 0);
    });

    return months;
  }, [items]);

  return (
    <div className="space-y-4" data-testid="finance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-[#27272A]">Financeiro</h1>
          <p className="text-[#71717A] text-sm mt-1">Receitas, despesas e resultado.</p>
        </div>
        <button data-testid="btn-new-tx" onClick={() => { setPreset("receita"); setShowModal(true); }} className="btn-primary-nc flex items-center gap-1 text-sm">
          <Plus size={16} /> Novo
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <MetricCard label="Receitas" value={brl(receitas)} icon={TrendingUp} tone="positive" tid="fin-receitas" />
        <MetricCard label="Despesas" value={brl(despesas)} icon={TrendingDown} tone="warning" tid="fin-despesas" />
        <MetricCard label="Resultado" value={brl(receitas - despesas)} tone={receitas - despesas >= 0 ? "positive" : "warning"} tid="fin-resultado" />
      </div>

      <div className="card-nc p-4" data-testid="finance-chart">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <h2 className="font-display font-bold text-base text-[#27272A]">Movimento financeiro</h2>
            <p className="text-[11px] text-[#71717A] mt-0.5">Receitas e despesas dos últimos 6 meses.</p>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-[#71717A]">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#4F7A61]" /> Receitas</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#B76E56]" /> Despesas</span>
          </div>
        </div>

        <div className="h-40">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} barGap={3}>
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: "#71717A" }}
              />
              <Tooltip
                formatter={(value, name) => [brl(value), name === "receitas" ? "Receitas" : "Despesas"]}
                contentStyle={{ borderRadius: 12, border: "1px solid #E4E4E7", fontSize: 12 }}
                cursor={{ fill: "#FAF5F2" }}
              />
              <Bar dataKey="receitas" fill="#4F7A61" radius={[5, 5, 0, 0]} maxBarSize={18} />
              <Bar dataKey="despesas" fill="#B76E56" radius={[5, 5, 0, 0]} maxBarSize={18} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="card-nc p-8 text-center" data-testid="finance-empty">
          <p className="text-[#71717A] mb-3">Nenhum lançamento ainda.</p>
          <button onClick={() => setShowModal(true)} className="btn-primary-nc">Novo lançamento</button>
        </div>
      ) : (
        <div className="grid gap-2">
          {items.map(t => (
            <div key={t.id} data-testid={`tx-${t.id}`} className="card-nc p-4 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${t.type === "receita" ? "bg-[#EBF2EE] text-[#4F7A61]" : "bg-[#F9ECE8] text-[#B76E56]"}`}>
                {t.type === "receita" ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
              </div>
              <div className="flex-1">
                <div className="font-semibold text-[#27272A]">{t.description || t.category}</div>
                <div className="text-xs text-[#71717A]">{fmtDateBR(t.date)} · {t.category}</div>
              </div>
              <div className={`font-mono-brand font-bold ${t.type === "receita" ? "text-[#4F7A61]" : "text-[#B76E56]"}`}>
                {t.type === "receita" ? "+" : "−"}{brl(t.amount)}
              </div>
              <button onClick={async () => { await api.delete(`/transactions/${t.id}`); load(); }} data-testid={`del-tx-${t.id}`} className="text-[#71717A] p-2"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <TxModal initialType={preset} onClose={() => setShowModal(false)}
          onSaved={async () => { setShowModal(false); await load(); toast.success("Lançamento salvo!"); }} />
      )}
    </div>
  );
}

function MetricCard({ label, value, icon: Icon, tone, tid }) {
  const tc = tone === "positive" ? "text-[#4F7A61]" : tone === "warning" ? "text-[#B76E56]" : "text-[#27272A]";
  return (
    <div className="card-nc p-3" data-testid={tid}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-[#71717A]">{label}</div>
      <div className={`text-base md:text-xl font-mono-brand font-bold ${tc} mt-1`}>{value}</div>
    </div>
  );
}

function TxModal({ initialType, onClose, onSaved }) {
  const [form, setForm] = useState({
    type: initialType, category: "atendimento",
    description: "", amount: 0, date: todayISO(), status: "pago",
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const cats = form.type === "receita"
    ? ["atendimento", "venda", "outro"]
    : ["material", "aluguel", "energia", "marketing", "transporte", "aplicativo", "impostos", "outro"];

  const save = async () => {
    if (form.amount <= 0) { toast.error("Informe um valor válido."); return; }
    setLoading(true);
    try { await api.post("/transactions", form); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  return (
    <Modal onClose={onClose} title="Novo lançamento" testid="modal-tx">
      <div className="grid grid-cols-2 gap-2">
        {["receita", "despesa"].map(t => (
          <button key={t} onClick={() => set("type", t)} data-testid={`tx-type-${t}`}
            className={`p-3 rounded-xl border-2 font-semibold capitalize ${form.type === t ? "border-[#682F3E] bg-[#F5ECE8]" : "border-[#E4E4E7]"}`}>
            {t}
          </button>
        ))}
      </div>
      <Field label="Categoria">
        <select data-testid="tx-cat" value={form.category} onChange={e => set("category", e.target.value)} className="input-nc">
          {cats.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      <Field label="Descrição"><input data-testid="tx-desc" value={form.description} onChange={e => set("description", e.target.value)} className="input-nc" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Valor (R$)"><input data-testid="tx-amount" type="number" step="0.01" value={form.amount} onChange={e => set("amount", Number(e.target.value))} className="input-nc" /></Field>
        <Field label="Data"><input data-testid="tx-date" type="date" value={form.date} onChange={e => set("date", e.target.value)} className="input-nc" /></Field>
      </div>
      <button data-testid="tx-save" onClick={save} disabled={loading} className="btn-primary-nc w-full">
        {loading ? "Salvando…" : "Salvar"}
      </button>
    </Modal>
  );
}
